<template>
  <label class="text-sm font-bold">
    Sélectionnez le signataire {{ props.suffixe
    }}<span class="text-jinius-red">*</span>
  </label>
  <div
    class="refonte-conventions-select select-formations"
    v-if="props.signataire_list && props.signataire_list.length > 0">
    <refonte-select
      v-bind:disabled="props.disabled"
      placeholder="Sélectionnez le signataire dans la liste"
      class="flex-grow"
      content_wrapper_class="signataire-custom-multiselect"
      v-bind:use_teleport="true"
      input_class="w-full"
      value_label="nomPrenom"
      v-bind:options="props.signataire_list"
      v-bind:value="
        props.signataire_list.find(
          (s) =>
            props.selected_signataire && props.selected_signataire.id === s.id
        )
      "
      v-on:input="(s) => props.select_signataire(s)" />
    <div
      class="ml-1 mt-2 flex items-center gap-2"
      v-if="props.has_admin_rights">
      <span class="info-add-participant-icon shrink-0 text-blue-600">
        <IconInformation />
      </span>
      <p class="text-xs">
        Signataire absent de la liste ?
        <button
          class="font-bold text-blue-600 hover:underline"
          v-on:click="goToInterlocuteursList">
          Ajouter un nouveau signataire
        </button>
      </p>
    </div>
    <div
      v-if="!props.has_admin_rights"
      class="mt-3 flex items-start gap-3 rounded border border-amber-100 bg-amber-50 p-3">
      <span class="info-icon mt-0.5 shrink-0 text-amber-600">
        <IconWarning20 />
      </span>
      <div class="text-amber-800">
        <div class="font-bold">Le signataire est absent de la liste ?</div>
        <div>Veuillez vous rapprocher de l'administrateur du compte.</div>
      </div>
    </div>
    <div
      class="ml-1 mt-2 flex items-center gap-2"
      v-if="props.open_signature_manuelle && showSignatureManuelle">
      <span class="info-add-participant-icon shrink-0 text-blue-600">
        <IconSignedV1 />
      </span>
      <p class="text-xs">
        <button
          class="hover:underline"
          v-on:click="openSignatureManuelleConfirm">
          Confirmation par téléphone portable impossible
        </button>
      </p>
    </div>
  </div>
  <template v-else>
    <div
      v-if="props.has_admin_rights"
      class="flex w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-blue-100/50 bg-refonte-pale-violet-2 p-8 text-center transition-all hover:border-blue-100">
      <div
        class="mb-3 flex h-12 w-12 items-center justify-center rounded-full border border-blue-100/50 bg-white shadow-sm">
        <i class="fas fa-user-plus text-lg text-blue-500"></i>
      </div>

      <h3 class="mb-1 text-sm font-semibold text-black">
        Aucun signataire disponible
      </h3>
      <p class="mb-4 max-w-sm text-xs leading-relaxed">
        Vous devez d'abord ajouter un signataire à ce compte pour pouvoir lancer
        la procédure.
      </p>

      <button
        v-on:click="goToInterlocuteursList"
        class="flex items-center gap-2 rounded-md border border-blue-600 bg-white px-5 py-2 text-sm font-medium text-blue-700 shadow-sm transition-all hover:bg-blue-600 hover:text-white">
        <i class="fas fa-plus text-xs"></i>
        Désigner un nouveau signataire
      </button>
    </div>
    <div
      v-else
      class="flex w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-blue-100/50 bg-refonte-pale-violet-2 p-8 text-center transition-all">
      <div
        class="mb-3 flex h-12 w-12 items-center justify-center rounded-full border border-blue-100/50 bg-white shadow-sm">
        <i class="fas fa-user-lock text-lg text-amber-500"></i>
      </div>

      <h3 class="mb-1 text-sm font-semibold text-black">
        Aucun signataire disponible
      </h3>
      <p class="mb-4 max-w-sm text-xs leading-relaxed">
        Aucun signataire n'est rattaché à ce compte.
        <br class="mb-2" />
        <span class="font-medium"
          >Veuillez vous rapprocher de votre administrateur pour en désigner un
          nouveau.</span
        >
      </p>
    </div>
    <div
      class="ml-1 flex items-center gap-2"
      v-if="props.open_signature_manuelle">
      <span class="info-add-participant-icon shrink-0 text-blue-600">
        <IconSignedV1 />
      </span>
      <p class="text-xs">
        <button
          class="hover:underline"
          v-on:click="openSignatureManuelleConfirm">
          Confirmation par téléphone portable impossible
        </button>
      </p>
    </div>
  </template>
  <signature-manuelle-confirm-modal
    v-if="props.open_signature_manuelle"
    v-bind:on_ok="props.open_signature_manuelle"
    v-bind:document="props.document_name"
    v-bind:show="showSignatureManuelleConfirmModal"
    v-bind:on_close="closeSignatureManuelleConfirm" />
</template>

<script setup lang="ts">
  import IconInformation from '@/assets/img/refonte/icons/information/icon-information.svg?component';
  import IconSignedV1 from '@/assets/img/refonte/icons/display/icon-signed-v1.svg?component';
  import IconWarning20 from '@/assets/img/refonte/icons/information/icon-warning-sign-20.svg?component';
  import { InterlocuteurSignataire } from '@/types/gestion/interlocuteurs-types';
  import RefonteSelect from '@/components/refonte/inputs/refonte-select.vue';

  import { computed, ref } from 'vue';
  import SignatureManuelleConfirmModal from '@/components/refonte/modals/signataire/signature-manuelle-confirm-modal.vue';

  const props = withDefaults(
    defineProps<{
      class?: string;
      suffixe?: string;
      document_name: string;
      signataire_list?: InterlocuteurSignataire[];
      has_admin_rights?: boolean;
      selected_signataire?: InterlocuteurSignataire;
      select_signataire: (s: InterlocuteurSignataire) => void;
      open_signature_manuelle?: Function;
      client_id?: number;
      disabled?: boolean;
    }>(),
    {
      class: '',
      disabled: false
    }
  );

  const showSignatureManuelleConfirmModal = ref<boolean>(false);
  const openSignatureManuelleConfirm = () => {
    showSignatureManuelleConfirmModal.value = true;
  };
  const closeSignatureManuelleConfirm = () => {
    showSignatureManuelleConfirmModal.value = false;
  };
  const goToInterlocuteursList = () => {
    window.open(
      `/gestion/interlocuteurs?clientIds=${props.client_id}`,
      '_blank'
    );
  };

  const showSignatureManuelle = computed(
    () =>
      props.signataire_list &&
      props.signataire_list.length > 0 &&
      props.signataire_list.every((s) => !s.telephone)
  );
</script>

<style lang="scss" scoped>
  .info-icon {
    @apply mr-2 flex shrink-0 items-center justify-center;
    width: 16px;
    height: 16px;

    :deep(svg) {
      @apply text-refonte_blue-600;
      width: 16px;
      height: 16px;
    }
  }
  .info-add-participant-icon {
    @apply flex shrink-0 items-center justify-center;
    width: 14px;
    height: 14px;

    :deep(svg) {
      @apply text-refonte_blue-600;
      width: 14px;
      height: 14px;
    }
  }
</style>
